import { format } from 'date-fns';
import { each, entries, isArray, isBoolean, isEmpty, isEqual, keys } from 'lodash';
import { bool, func } from 'prop-types';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';

import { GiftcardsFilters, Icon, LeisuresFilters, SportFilters, TravelsFilters, useProductsContext } from 'components';
import { leisureDefaultValues, sportDefaultValues, travelDefaultValues } from 'constants';
import { PAGE_TYPES } from 'constants';
import { giftcardDefaultValues } from 'constants';
import { routes } from 'router';

import 'assets/styles/components/products/filters.scss';

const minSearchParams = { leisures: 1, travels: 2, sport: 2 };

const ProductsFilters = ({ setShowFilters, showFilters, fromHomepage = false }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const {
    pageType,
    setPage,
    setProductsTravels,
    loadingTravels,
    setProductsLeisures,
    loadingLeisures,
    sportCategoryId,
    loadingGiftcards,
    setGiftcards
  } = useProductsContext();

  const { handleSubmit, reset } = useFormContext();

  const resetFilters = () => {
    // Prevent reset if filters are the same as default values
    const minParamsSize = searchParams?.size <= minSearchParams[pageType];
    const preventTravelReset =
      pageType === PAGE_TYPES.travels && searchParams.get('tourOperatorId') === 'touloisirs' && minParamsSize;
    const preventLeisureReset = pageType === PAGE_TYPES.leisures && minParamsSize;
    const preventSportReset = pageType === PAGE_TYPES.sport && minParamsSize;

    if (preventTravelReset || preventLeisureReset || preventSportReset) return;

    // reset form and pagination
    reset();
    setPage(1);

    // Close filters view on mobile
    showFilters && setShowFilters(false);

    if (pageType === PAGE_TYPES.travel) setProductsTravels([]);
    if ([PAGE_TYPES.leisures, PAGE_TYPES.sport].includes(pageType)) setProductsLeisures([]);
    if (pageType === PAGE_TYPES.giftcards) setGiftcards([]);

    // reset url params
    setSearchParams({
      type: pageType,
      ...(pageType === PAGE_TYPES.travels && { tourOperatorId: 'touloisirs' }),
      ...(pageType === PAGE_TYPES.sport && { category: sportCategoryId })
    });
  };

  const formatURLParams = (key, data) => {
    const isSelectKey = ['country', 'departure', 'tourOperatorId', 'place'].includes(key);
    const isDateKey = key === 'dates';

    if (isSelectKey) {
      /**
       * For leisures products the full name of the place (e.g : Paris) must be sent to the API instead of the id
       * because the products catalogue is not the same for travels and leisures
       * For travels, the id of the place (e.g : 75) must be sent to the API
       */
      const newValue = key === 'place' ? data?.label : data?.id;
      newValue && searchParams.set(key, newValue);
    } else if (isDateKey) {
      data[0] && searchParams.set('beginDate', format(data[0], 'yyyy-MM-dd'));
      data[1] && searchParams.set('endDate', format(data[1], 'yyyy-MM-dd'));
    } else {
      if (key === 'leisureCategory' || key === 'sportCategory') {
        /*
          categoryCodes is a string of comma separated values
          e.g: category=AB,CD

          subCategories is a string of hyphen separated values for each category
          Each subCategories of different categories are separated by a comma
          e.g: subCategory=AA-BB,CC-DD
          AA and BB are subCategories of category AB
          CC and DD are subCategories of category CD
        */
        let categoryParams = '';
        let subCategoryParams = '';
        entries(data).forEach(([category, subCategories]) => {
          if (subCategories.length === 0 || !isArray(subCategories)) return;
          categoryParams += `${category},`;
          subCategoryParams += `${subCategories.join('-')},`;
        });
        categoryParams = categoryParams.slice(0, -1);
        subCategoryParams = subCategoryParams.slice(0, -1);
        if (key === 'sportCategory' && isEmpty(categoryParams) && sportCategoryId) {
          searchParams.set('category', sportCategoryId);
        }
        !isEmpty(categoryParams) && searchParams.set('category', categoryParams);
        !isEmpty(subCategoryParams) && searchParams.set('subCategory', subCategoryParams);
      } else {
        searchParams.set(key, data);
      }
    }

    return searchParams;
  };

  const onSubmit = (formValues) => {
    if (fromHomepage) {
      handleGiftcardsSearch(formValues);
      return;
    }

    if (loadingTravels || loadingLeisures || loadingGiftcards) return;

    const oldSearchParams = Object.fromEntries(searchParams.entries());

    // Reset url search params to avoid keeping old filters on new search
    each(keys(oldSearchParams), (key) => searchParams.delete(key));

    // Set page type to redirect to the right result page
    searchParams.set('type', pageType);
    let defaultValues = {};
    if (pageType === PAGE_TYPES.travels) defaultValues = travelDefaultValues;
    if (pageType === PAGE_TYPES.leisures) defaultValues = leisureDefaultValues;
    if (pageType === PAGE_TYPES.sport) defaultValues = sportDefaultValues;
    if (pageType === PAGE_TYPES.giftcards) defaultValues = giftcardDefaultValues;

    // Add products filters to url search params
    each(keys(defaultValues), (key) => {
      const value = formValues[key];

      if (key === 'subsidizedOffers') {
        value ? searchParams.set('subsidizedOffers', 'oui') : searchParams.delete('subsidizedOffers');
      } else {
        if (key === 'type' || value === false || (!isBoolean(value) && isEmpty(value))) return;
        formatURLParams(key, value);
      }
    });

    // Prevent API call if search params are the same
    const newSearchParams = Object.fromEntries(searchParams.entries());
    if (isEqual(oldSearchParams, newSearchParams)) return;

    setSearchParams(searchParams);

    // Close filters view on mobile
    showFilters && setShowFilters(false);
  };

  const handleGiftcardsSearch = (formValues) => {
    if (loadingGiftcards) return;
    const giftcardParams = createSearchParams({
      keywords: formValues.keywords,
      category: formValues.category?.join(','),
      type: PAGE_TYPES.giftcards
    }).toString();
    navigate({ pathname: routes.results.path, search: giftcardParams });
  };

  return (
    <form className="filters-form" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex-center-between flex-wrap gap-3 pt-8 px-6 lg:py-8 lg:border-b lg:border-gray-200">
        <h3 className="h3 underlined-blue hidden lg:block">
          {t(pageType === PAGE_TYPES.travels ? 'search' : 'filters')}
        </h3>

        <button type="button" className="lg:hidden" onClick={() => setShowFilters(false)}>
          <Icon name="arrow-right-long" classes="rotate-180" />
          <span className="sr-only">{t('buttons.close')}</span>
        </button>

        <h3 className="h3 lg:hidden">{t('filters')}</h3>

        <button
          type="button"
          className="btn-white"
          onClick={resetFilters}
          disabled={loadingTravels || loadingLeisures || loadingGiftcards}
          data-test-id="resetFiltersBtn"
        >
          {t('buttons.erase')}
        </button>
      </div>

      {pageType === PAGE_TYPES.travels && <TravelsFilters />}
      {pageType === PAGE_TYPES.leisures && <LeisuresFilters />}
      {pageType === PAGE_TYPES.sport && <SportFilters />}
      {pageType === PAGE_TYPES.giftcards && <GiftcardsFilters />}

      <div className="submit-container py-6">
        <button
          type="submit"
          className="btn secondary w-full uppercase"
          disabled={loadingTravels || loadingLeisures || loadingGiftcards}
          data-test-id="filtersSubmitBtn"
        >
          {t('buttons.search')}
        </button>
      </div>
    </form>
  );
};

ProductsFilters.propTypes = {
  setShowFilters: func.isRequired,
  showFilters: bool.isRequired,
  fromHomepage: bool
};

export default ProductsFilters;
